import React from "react";

import type { ProcessedSameEventMultiSelection } from "@/hooks/firestore/betting/useEntries";

import cx from "classnames";
import { titleCase } from "title-case";
import { useTitle } from "@/hooks/graphql/useTitles";
import {
  type BettingEventStatus,
  useBettingEventStatuses,
} from "@/hooks/firestore/betting/useBettingEventStatuses";
import {
  getSelectionClickHandler,
  getSelectionStatus,
} from "@/features/entries/util/selection-helpers";
import { BetTrackerLogo } from "./BetTrackerLogo";
import { CompetitorAvatar } from "features/betting/components/CompetitorAvatar";
import { ShinyText } from "@picklebet/ui";
import { RollingCounter } from "@picklebet/ui";
import {
  getValidSportPeriods,
  SPORTS_FOR_HOME_AWAY_FALLBACKS,
} from "features/entries/util/bet-tracker-helpers";

import * as styles from "./ScoreBoard.module.scss";

const SCOREBOARD_HIDDEN =
  process.env.GATSBY_HIDE_BET_TRACKER_SCOREBOARD === "true";

interface ScoreBoardProps {
  selection: ProcessedSameEventMultiSelection;
  hasTrackableBets?: boolean;
  trackerDisabled?: boolean;
}

const ScoreBoard = ({
  selection,
  hasTrackableBets,
  trackerDisabled,
}: ScoreBoardProps) => {
  const isSRM = selection.event.eventType === "RACE";
  const eventName = selection.event.eventName;
  const status = getSelectionStatus(selection);

  const { bettingEventStatus, isLoading } = useBettingEventStatuses(
    selection.event.eventId,
  );

  const isPrematch =
    status?.message !== "LIVE" && selection.result === "UNDECIDED";

  const periods = getValidSportPeriods(selection.event.sport);

  const isInvalidSport = periods.length === 0;

  const teamNamesArray = eventName?.split(/ vs.? /, 2);

  const title = useTitle(selection.event.sport).name;

  const seasonName = isSRM
    ? title
    : selection.event.attributes?.seasonName ?? "";

  const raceNumber = selection.event.attributes?.raceNumber;

  const hideScoreboard =
    SCOREBOARD_HIDDEN ||
    isSRM ||
    isPrematch ||
    isInvalidSport ||
    trackerDisabled ||
    (Object.keys(bettingEventStatus?.periodScores ?? {}).length === 0 &&
      !isLoading);

  const showHomeAwayFallback = SPORTS_FOR_HOME_AWAY_FALLBACKS.includes(
    selection.event.sport,
  );

  const onClickEvent = getSelectionClickHandler(selection);

  return (
    <div>
      {hasTrackableBets && !trackerDisabled && (
        <BetTrackerLogo isAboveScoreBoard />
      )}

      <div
        onClick={onClickEvent}
        className={cx(styles.scoreBoard, {
          [styles.clickable]: !!onClickEvent,
          [styles.noBetTracker]: !hasTrackableBets || trackerDisabled,
          [styles.isPrematch]: isPrematch && !isSRM,
          [styles.isSRM]: isSRM,
          [styles.halves]: periods.length === 2 && !hideScoreboard,
          [styles.quarters]: periods.length === 4 && !hideScoreboard,
          [styles.thirds]: periods.length === 3 && !hideScoreboard,
        })}
      >
        <div className={cx(styles.competition)}>
          {isSRM ? "Racing" : title}: {seasonName}
        </div>

        {isSRM ? (
          <div className={cx(styles.cell, styles.header)}>
            {selection.event.attributes?.distance?.toUpperCase() ?? ""}
          </div>
        ) : (
          !hideScoreboard &&
          periods.map((p) => (
            <div className={cx(styles.cell, styles.header)} key={p}>
              {p}
            </div>
          ))
        )}

        {!hideScoreboard && (
          <>
            <div className={styles.divider} />
            <div className={cx(styles.cell, styles.header)}>T</div>
          </>
        )}

        {isSRM ? (
          <div>
            <span className={styles.team}>
              <div className={styles.logo}>R{raceNumber}</div>
              {selection.event.attributes?.venueName}
            </span>
            <div className={styles.eventName}>
              {titleCase(selection.event.eventName?.toLowerCase() ?? "")}
            </div>
          </div>
        ) : (
          <div className={cx(styles.team, styles.showVs)}>
            {bettingEventStatus?.homeCompetitor ? (
              <CompetitorAvatar
                competitor={{ ...bettingEventStatus?.homeCompetitor }}
                className={cx(styles.logo, styles.abbreviation)}
              />
            ) : (
              <div className={styles.logo}>
                {showHomeAwayFallback ? "H" : ""}
              </div>
            )}
            <ShinyText
              className={styles.teamName}
              overlayClassName={styles.shinyTeamName}
              trigger={bettingEventStatus?.homeScore ?? 0}
              duration={2}
            >
              {teamNamesArray?.[0] ?? ""}
            </ShinyText>
          </div>
        )}

        {!hideScoreboard && (
          <Scores
            team="homeScore"
            periodCount={periods.length}
            bettingEventStatus={bettingEventStatus}
          />
        )}

        {!isSRM && (
          <div className={styles.team}>
            {bettingEventStatus?.awayCompetitor ? (
              <CompetitorAvatar
                competitor={{ ...bettingEventStatus?.awayCompetitor }}
                className={cx(styles.logo, styles.abbreviation)}
              />
            ) : (
              <div className={styles.logo}>
                {showHomeAwayFallback ? "A" : ""}
              </div>
            )}
            <ShinyText
              className={styles.teamName}
              overlayClassName={styles.shinyTeamName}
              trigger={bettingEventStatus?.awayScore ?? 0}
              duration={2}
            >
              {teamNamesArray?.[1] ?? ""}
            </ShinyText>
          </div>
        )}

        {!hideScoreboard && (
          <Scores
            team="awayScore"
            periodCount={periods.length}
            bettingEventStatus={bettingEventStatus}
          />
        )}
      </div>
    </div>
  );
};

interface ScoresProps {
  team: "homeScore" | "awayScore";
  periodCount: number;
  bettingEventStatus: BettingEventStatus | undefined;
}

const Scores = ({ team, periodCount, bettingEventStatus }: ScoresProps) => {
  const periodScores = Array.from(
    { length: periodCount },
    (_, i) => bettingEventStatus?.periodScores?.[i + 1]?.[team],
  );

  return (
    <>
      {periodScores.map((score, index) => (
        <div
          key={index}
          className={cx(styles.cell, { [styles.emptyCell]: score == null })}
        >
          {score != null ? (
            <RollingCounter className={styles.counter} value={score} />
          ) : (
            "—"
          )}
        </div>
      ))}
      <div className={styles.divider} />
      <div
        className={cx(styles.cell, {
          [styles.emptyCell]:
            bettingEventStatus?.name === "Not started" ||
            bettingEventStatus?.[team] === undefined,
        })}
      >
        {bettingEventStatus?.name === "Not started" ? (
          "—"
        ) : bettingEventStatus?.[team] !== undefined ? (
          <RollingCounter
            className={styles.counter}
            value={bettingEventStatus?.[team] ?? 0}
          />
        ) : (
          "—"
        )}
      </div>
    </>
  );
};

export { ScoreBoard };
